/**
 * This service is used to get the student data for a specific student
 * It is used to display the student data in the student page
 * It is also used to display the student data in the student page
 * It is also used to display the student data in the student page
 */

import {
  PutCommand,
  QueryCommand,
  GetCommand,
  DeleteCommand,
  UpdateCommand
} from "@aws-sdk/lib-dynamodb";
import { dynamoDB, ddbClient } from "../firebase/config";
import {
  DescribeTableCommand,
  CreateTableCommand,
} from "@aws-sdk/client-dynamodb";
import posthog from 'posthog-js';

const fileCacheTableName = "file_cache"; // Updated to reflect all files caching
const fileCachePartitionKey = "global";

export const ensureStudentTableExists = async () => {
  try {
    await ddbClient.send(new DescribeTableCommand({ TableName: "Students" }));
  } catch (error) {
    if (error.name === "ResourceNotFoundException") {
      try {
        await ddbClient.send(
          new CreateTableCommand({
            TableName: "Students",
            AttributeDefinitions: [
              { AttributeName: "PK", AttributeType: "S" },
              { AttributeName: "SK", AttributeType: "S" },
              { AttributeName: "GSI1PK", AttributeType: "S" },
              { AttributeName: "GSI1SK", AttributeType: "S" },
            ],
            KeySchema: [
              { AttributeName: "PK", KeyType: "HASH" },
              { AttributeName: "SK", KeyType: "RANGE" },
            ],
            GlobalSecondaryIndexes: [
              {
                IndexName: "StudentIndex",
                KeySchema: [
                  { AttributeName: "GSI1PK", KeyType: "HASH" },
                  { AttributeName: "GSI1SK", KeyType: "RANGE" },
                ],
                Projection: { ProjectionType: "ALL" },
              },
            ],
            BillingMode: "PAY_PER_REQUEST",
          }),
        );
      } catch (createError) {
        console.error("Error creating Students table:", createError);
        posthog.captureException(createError, {
          tags: {
            service: 'student_service',
            operation: 'ensure_student_table_exists'
          }
        });
        throw createError;
      }
    } else {
      console.error("Error describing Students table:", error);
      posthog.captureException(error, {
        tags: {
          service: 'student_service',
          operation: 'ensure_student_table_exists'
        }
      });
      throw error;
    }
  }
};

export const upsertStudent = async (studentData) => {
  try {
    await ensureStudentTableExists();

    // Normalize email to lowercase for consistent storage
    const normalizedEmail = studentData.email.toLowerCase();

    const params = {
      TableName: "Students",
      Item: {
        PK: "STUDENT#ALL",
        SK: `STUDENT#${normalizedEmail}`,
        name: studentData.name,
        email: normalizedEmail, // Store normalized email
        batch: studentData.batch,
        centres: studentData.centres || [],
        subjects: studentData.subjects || [],
        enrollmentDate: studentData.enrollmentDate,
        imageUrl: studentData.imageUrl || "",
        class: studentData.class || "",
        board: studentData.board || "",
        mobile: studentData.mobile || "",
        address: studentData.address || "",
        status: studentData.status || "inactive",
        dob: studentData.dob || "",
        school: studentData.school || "",
        amountPending: studentData.amountPending || 0,
        paymentType: studentData.paymentType || "lumpsum",
        payments: studentData.payments || [],
        monthlyInstallment: studentData.monthlyInstallment || 0,
        updatedAt: new Date().toISOString(),
        chatIds: studentData.chatIds || [],
        GSI1PK: `BATCH#${studentData.batch}`,
        GSI1SK: `STUDENT#${normalizedEmail}`,
      },
    };

    await dynamoDB.send(new PutCommand(params));
    return { id: normalizedEmail };
  } catch (error) {
    console.error("Error upserting student:", error);
    posthog.captureException(error, {
      tags: {
        service: 'student_service',
        operation: 'upsert_student',
        student_email: studentData.email
      },
      extra: {
        batch: studentData.batch,
        status: studentData.status
      }
    });
    throw error;
  }
};

//delete student
export const deleteStudent = async (email) => {
  try {
    // Normalize email to lowercase for consistent lookup
    const normalizedEmail = email.toLowerCase();
    
    const params = {
      TableName: "Students",
      Key: {
        PK: "STUDENT#ALL",
        SK: `STUDENT#${normalizedEmail}`,
      },
    };
    await dynamoDB.send(new DeleteCommand(params));
    return { id: normalizedEmail };
  } catch (error) {
    console.error("Error deleting student:", error);
    posthog.captureException(error, {
      tags: {
        service: 'student_service',
        operation: 'delete_student',
        student_email: email
      }
    });
    throw error;
  }
};

export const getStudentByEmail = async (email) => {
  try {
    // Normalize email to lowercase for consistent lookup
    const normalizedEmail = email.toLowerCase();
    
    const params = {
      TableName: "Students",
      KeyConditionExpression: "PK = :pk AND SK = :sk",
      ExpressionAttributeValues: {
        ":pk": "STUDENT#ALL",
        ":sk": `STUDENT#${normalizedEmail}`,
      },
    };

    const response = await dynamoDB.send(new QueryCommand(params));
    return response.Items?.[0];
  } catch (error) {
    console.error("Error getting student by email:", error);
    posthog.captureException(error, {
      tags: {
        service: 'student_service',
        operation: 'get_student_by_email',
        student_email: email
      }
    });
    throw error;
  }
};

export const getStudentsByBatch = async (batch) => {
  try {
    const params = {
      TableName: "Students",
      IndexName: "StudentIndex",
      KeyConditionExpression: "GSI1PK = :batchId",
      ExpressionAttributeValues: {
        ":batchId": `BATCH#${batch}`,
      },
    };

    const response = await dynamoDB.send(new QueryCommand(params));
    return response.Items || [];
  } catch (error) {
    console.error("Error getting students by batch:", error);
    posthog.captureException(error, {
      tags: {
        service: 'student_service',
        operation: 'get_students_by_batch',
        batch: batch
      }
    });
    throw error;
  }
};

export const getAllStudents = async () => {
  try {
    const params = {
      TableName: "Students",
      KeyConditionExpression: "PK = :pk",
      ExpressionAttributeValues: {
        ":pk": "STUDENT#ALL",
      },
    };

    const response = await dynamoDB.send(new QueryCommand(params));
    return response.Items || [];
  } catch (error) {
    console.error("Error getting all students:", error);
    posthog.captureException(error, {
      tags: {
        service: 'student_service',
        operation: 'get_all_students'
      }
    });
    throw error;
  }
};

export const ensureFileCacheTableExists = async () => {
  // Updated to handle all files caching
  return true;
  try {
    await ddbClient.send(
      new DescribeTableCommand({ TableName: fileCacheTableName }),
    ); // Use ddbClient
    console.log("File cache table exists.");
    return true;
  } catch (error) {
    if (error.name === "ResourceNotFoundException") {
      console.log("File cache table does not exist. Creating...");
      try {
        const createParams = {
          TableName: fileCacheTableName,
          KeySchema: [{ AttributeName: "partitionKey", KeyType: "HASH" }],
          AttributeDefinitions: [
            { AttributeName: "partitionKey", AttributeType: "S" },
          ],
          ProvisionedThroughput: {
            ReadCapacityUnits: 5, // Adjust as needed
            WriteCapacityUnits: 5, // Adjust as needed
          },
        };
        const command = new CreateTableCommand(createParams);
        await ddbClient.send(command); // Use ddbClient.send
        console.log("File cache table created successfully!");

        // Wait for the table to be active
        let tableActive = false;
        while (!tableActive) {
          try {
            const describeTableResponse = await ddbClient.send(
              new DescribeTableCommand({ TableName: fileCacheTableName }),
            );
            tableActive = describeTableResponse.Table.TableStatus === "ACTIVE";
            if (!tableActive) {
              console.log("Waiting for table to become active...");
              await new Promise((resolve) => setTimeout(resolve, 5000)); // Wait 5 seconds
            }
          } catch (describeError) {
            console.error("Error describing table:", describeError);
            return false;
          }
        }
        console.log("File cache table is now active.");
        return true;
      } catch (createErr) {
        console.error("Error creating file cache table:", createErr);
        return false;
      }
    } else {
      console.error("Error describing file cache table:", error);
      return false;
    }
  }
};

export const storeAllFiles = async (allFiles) => {
  const tableExists = await ensureFileCacheTableExists();
  if (!tableExists) return;

  // Convert Date objects to ISO strings for DynamoDB compatibility
  const serializedFiles = allFiles.map(file => ({
    ...file,
    lastModified: file.lastModified instanceof Date 
      ? file.lastModified.toISOString() 
      : file.lastModified
  }));

  const params = {
    TableName: fileCacheTableName, // Updated table name
    Item: {
      partitionKey: fileCachePartitionKey, // Updated partition key
      allFiles: serializedFiles, // Store all files (videos + PDFs) with serialized dates
      lastUpdated: new Date().toISOString(), // Add timestamp for cache invalidation
    },
  };

  try {
    console.log("Storing all files...");
    console.log(params);
    await dynamoDB.send(new PutCommand(params)); // Use PutCommand
    console.log("All files stored successfully!");
  } catch (error) {
    console.error("Error storing all files:", error);
    posthog.captureException(error, {
      tags: {
        service: 'student_service',
        operation: 'store_all_files'
      },
      extra: {
        file_count: allFiles.length
      }
    });
  }
};


export const retrieveAllFiles = async () => {
  const tableExists = await ensureFileCacheTableExists();
  if (!tableExists) return null;

  const params = {
    TableName: fileCacheTableName, // Updated table name
    Key: {
      partitionKey: fileCachePartitionKey, // Updated partition key
    },
  };

  try {
    const data = await dynamoDB.send(new GetCommand(params)); // Use GetCommand

    if (data.Item && data.Item.allFiles) {
      console.log("All files retrieved successfully.");
      // Convert ISO strings back to Date objects
      const deserializedFiles = data.Item.allFiles.map(file => ({
        ...file,
        lastModified: file.lastModified ? new Date(file.lastModified) : file.lastModified
      }));
      return deserializedFiles;
    } else {
      console.log("No files found in cache.");
      return null;
    }
  } catch (error) {
    console.error("Error retrieving all files:", error);
    posthog.captureException(error, {
      tags: {
        service: 'student_service',
        operation: 'retrieve_all_files'
      }
    });
    return null;
  }
};

export const getStudentStatusCount = async () => {
  const students = await getAllStudents();
  return students.reduce((acc, student) => {
    const status = student.status || "inactive";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
};

export const updateStudentStatus = async (email, status) => {
  try {
    // Normalize email to lowercase for consistent lookup
    const normalizedEmail = email.toLowerCase();

    const params = {
      TableName: "Students",
      Key: {
        PK: "STUDENT#ALL",
        SK: `STUDENT#${normalizedEmail}`,
      },
      UpdateExpression: "SET #status = :status, updatedAt = :updatedAt",
      ExpressionAttributeNames: {
        "#status": "status",
      },
      ExpressionAttributeValues: {
        ":status": status,
        ":updatedAt": new Date().toISOString(),
      },
      ReturnValues: "ALL_NEW",
    };

    const response = await dynamoDB.send(new UpdateCommand(params));
    return response.Attributes;
  } catch (error) {
    console.error("Error updating student status:", error);
    posthog.captureException(error, {
      tags: {
        service: 'student_service',
        operation: 'update_student_status',
        student_email: email,
        new_status: status
      }
    });
    throw error;
  }
};

export const getActiveStudents = async () => {
  const students = await getAllStudents();
  return students.filter((student) => student.status === "active");
};

export const getStudentsByFilter = async (filters) => {
  const students = await getAllStudents();
  return students.filter((student) => {
    return (
      (!filters.batch || student.batch === filters.batch) &&
      (!filters.centre || student.centres.includes(filters.centre)) &&
      (!filters.status || student.status === filters.status)
    );
  });
};

export const updateStudentAttendance = async (email, attendanceRecord) => {
  const { subject, topic, status } = attendanceRecord;
  const timestamp = new Date().toISOString();

  // Normalize email to lowercase for consistent lookup
  const normalizedEmail = email.toLowerCase();

  // First, get the current attendance data
  const getParams = {
    TableName: "Students",
    Key: {
      PK: "STUDENT#ALL",
      SK: `STUDENT#${normalizedEmail}`,
    },
    ProjectionExpression: "attendance",
  };

  try {
    const currentData = await dynamoDB.send(new GetCommand(getParams));
    const currentAttendance = currentData.Item?.attendance || {};

    // Prepare the new attendance record
    const newRecord = {
      status,
      timestamp,
      date: new Date().toLocaleDateString(),
    };

    // Build the updated attendance structure
    const updatedAttendance = {
      ...currentAttendance,
      [subject]: {
        ...currentAttendance[subject],
        [topic]: [...(currentAttendance[subject]?.[topic] || []), newRecord],
      },
    };

    // Update with the combined data
    const updateParams = {
      TableName: "Students",
      Key: {
        PK: "STUDENT#ALL",
        SK: `STUDENT#${normalizedEmail}`,
      },
      UpdateExpression: "SET attendance = :attendance",
      ExpressionAttributeValues: {
        ":attendance": updatedAttendance,
      },
    };

    await dynamoDB.send(new UpdateCommand(updateParams));
  } catch (error) {
    console.error(`Error updating attendance for student ${email}:`, error);
    posthog.captureException(error, {
      tags: {
        service: 'student_service',
        operation: 'update_student_attendance',
        student_email: email,
        subject: subject,
        topic: topic
      },
      extra: {
        status: status
      }
    });
    throw error;
  }
};
